import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * The crew's time budget for the step they are on, and for the job as a whole.
 *
 * Two components, deliberately, because they render in two different places (see below):
 * `NavStepTimerToggleComponent` is the always-visible clock in the top bar, and
 * `NavStepTimerDetailComponent` is the pair of bars it reveals.
 *
 * Shell chrome, not navigator nodes — rendered by `NavEventNavigatorComponent` alongside the top bar
 * and the footer, and a navigator script cannot place them. That is why they live here rather than in
 * `components/nav/`, whose contents are all script-dispatched components extending `NavBaseComponent`
 * with a `[node]` input. No tag name, no case in `navigator-node.component.ts`, no declaration in
 * `functions/navigator.d.ts`.
 *
 * Deliberately dumb: no store, no interval, no clock. Every value is an input and the parent owns the
 * arithmetic, because the numbers come from state only the shell has — `_stepStartTime` (restored
 * from the store, so elapsed survives a reload), the steps array with each step's `maxTime`, and the
 * confirm/undo hooks that close one step and open the next. Keeping the clock out here also means
 * both can be tested without a fake timer.
 *
 * **Why split.** The first version was one component rendering the clock and, beneath it, the detail
 * — a single block on its own row under the progress bar. The clock was then asked to sit in the top
 * bar itself, to the left of HELP, with the expanded behaviour unchanged. Those are two different
 * parents in the template, so one element cannot do both without either duplicating every input or
 * making the detail an absolutely-positioned overlay, which would change how it interacts with the
 * content below it. Two small components keep the expand behaviour exactly as it was.
 */

/** `MM:SS`, or `H:MM:SS` once past an hour. */
function formatDuration(totalSeconds: number): string {
    const s = Math.max(0, Math.floor(totalSeconds));
    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Neutral until 90% of the budget, amber to 100%, red over.
 *
 * The 100% threshold is deliberate and differs from the retired `StepTimer`, which only reddened at
 * 110%. "Red when it goes over the limit" is what a crew reads the colour for; a step that is over
 * budget but still green is worse than no colour at all.
 */
function timerStatus(elapsed: number, budget: number): string {
    if (budget <= 0) { return 'on-track'; }
    const ratio = elapsed / budget;
    if (ratio > 1) { return 'over'; }
    if (ratio >= 0.9) { return 'behind'; }
    return 'on-track';
}

function timerPct(elapsed: number, budget: number): number {
    if (budget <= 0) { return 0; }
    return Math.min(100, (elapsed / budget) * 100);
}

/** Shared colour rules. Kept in one string so the two components cannot drift apart. */
const STATUS_STYLES = `
    .on-track { color: #15803d; }
    .behind   { color: #a16207; }
    .over     { color: #b91c1c; }
`;

/**
 * The clock, sized to sit inside the 48px top bar to the left of HELP.
 *
 * That row is `display: flex` with `.topbar-info` on `flex: 1; min-width: 0` and `.topbar-title`
 * already ellipsis-truncating on narrow phones, so every pixel taken here comes out of the step
 * title. Hence the tight padding and `flex-shrink: 0`: the clock keeps its digits and the title
 * gives up characters, which is the right way round — a half-read title is still readable, a
 * half-read clock is not.
 */
@Component({
    selector: 'app-nav-step-timer-toggle',
    standalone: true,
    imports: [CommonModule],
    styles: [`
        :host { display: block; flex-shrink: 0; }
        .timer-toggle {
            display: flex; align-items: center; gap: 0.3rem;
            height: 100%; min-height: 44px; padding: 0 0.5rem; border-radius: 8px;
            background: none; border: none; cursor: pointer;
            font-family: inherit; font-size: 0.9rem; font-weight: 700;
            font-variant-numeric: tabular-nums; white-space: nowrap;
        }
        .timer-toggle i.pi-clock { font-size: 0.8rem; font-weight: 400; }
        .timer-toggle .chevron { font-size: 0.65rem; color: #999; font-weight: 400; }
    ` + STATUS_STYLES],
    template: `
        <button
            type="button"
            class="timer-toggle"
            [attr.aria-expanded]="expanded"
            aria-label="Step timer"
            (click)="toggled.emit()">
            <i class="pi pi-clock"
               [class.on-track]="status === 'on-track'"
               [class.behind]="status === 'behind'"
               [class.over]="status === 'over'"></i>
            <span [class.on-track]="status === 'on-track'"
                  [class.behind]="status === 'behind'"
                  [class.over]="status === 'over'">{{ display }}</span>
            <span class="chevron">{{ expanded ? '▴' : '▾' }}</span>
        </button>
    `,
})
export class NavStepTimerToggleComponent {
    /** Seconds on the current step. */
    @Input() elapsedSec = 0;
    /** The current step's `maxTime`. Zero or absent means "no budget" — elapsed shows uncoloured. */
    @Input() budgetSec = 0;
    @Input() expanded = false;
    @Output() toggled = new EventEmitter<void>();

    get status(): string { return timerStatus(this.elapsedSec, this.budgetSec); }
    get display(): string { return formatDuration(this.elapsedSec); }
}

/**
 * The two bars the clock reveals. Rendered in flow, directly under the progress bar, so expanding
 * pushes the step content down rather than covering it — unchanged from the original single-block
 * version, which is what "the rest of the behaviour should not change" requires.
 */
@Component({
    selector: 'app-nav-step-timer-detail',
    standalone: true,
    imports: [CommonModule],
    styles: [`
        :host { display: block; background: white; border-bottom: 1px solid #e0e0e0; }
        .detail { padding: 0.4rem 1rem 0.7rem; }
        .detail-row {
            display: flex; align-items: center; gap: 0.6rem;
            font-size: 0.78rem; color: #666; margin-top: 0.35rem;
        }
        .detail-label { flex: 0 0 5.5rem; text-transform: uppercase; letter-spacing: 0.04em; }
        .detail-figures { flex: 0 0 8.5rem; font-variant-numeric: tabular-nums; color: #1a1a1a; }
        .detail-bar-wrap {
            flex: 1; background: #f0f0f0; border-radius: 3px; height: 5px; overflow: hidden;
        }
        /*
         * display:block is load-bearing, not decoration. These are divs, but an inline box ignores
         * width and height outright — which is exactly how this shipped first time: bars permanently
         * grey, never moving. The wrap happened to work because a flex item is blockified
         * automatically; the inner bar is not a flex item, so nothing blockified it. (No backticks
         * in here — this block sits inside a template literal.)
         */
        .detail-bar { display: block; height: 100%; border-radius: 3px; transition: width 1s linear; }
        .detail-bar.on-track { background: #16a34a; }
        .detail-bar.behind   { background: #d97706; }
        .detail-bar.over     { background: #dc2626; }
    ` + STATUS_STYLES],
    template: `
        <div class="detail">
            <div class="detail-row">
                <span class="detail-label">This step</span>
                <span class="detail-figures">
                    {{ fmt(elapsedSec) }}<ng-container *ngIf="budgetSec > 0"> / {{ fmt(budgetSec) }}</ng-container>
                </span>
                <div class="detail-bar-wrap" *ngIf="budgetSec > 0">
                    <div class="detail-bar"
                         [class.on-track]="stepStatus === 'on-track'"
                         [class.behind]="stepStatus === 'behind'"
                         [class.over]="stepStatus === 'over'"
                         [style.width.%]="stepPct"></div>
                </div>
            </div>

            <div class="detail-row" *ngIf="totalBudgetSec > 0">
                <span class="detail-label">Whole job</span>
                <span class="detail-figures">{{ fmt(totalElapsedSec) }} / {{ fmt(totalBudgetSec) }}</span>
                <div class="detail-bar-wrap">
                    <div class="detail-bar"
                         [class.on-track]="totalStatus === 'on-track'"
                         [class.behind]="totalStatus === 'behind'"
                         [class.over]="totalStatus === 'over'"
                         [style.width.%]="totalPct"></div>
                </div>
            </div>
        </div>
    `,
})
export class NavStepTimerDetailComponent {
    /** Seconds on the current step. */
    @Input() elapsedSec = 0;
    /** The current step's `maxTime`. */
    @Input() budgetSec = 0;
    /** Seconds since the job started — wall clock, not a sum of step durations. */
    @Input() totalElapsedSec = 0;
    /** Sum of every step's `maxTime`. */
    @Input() totalBudgetSec = 0;

    get stepStatus(): string { return timerStatus(this.elapsedSec, this.budgetSec); }
    get totalStatus(): string { return timerStatus(this.totalElapsedSec, this.totalBudgetSec); }
    get stepPct(): number { return timerPct(this.elapsedSec, this.budgetSec); }
    get totalPct(): number { return timerPct(this.totalElapsedSec, this.totalBudgetSec); }

    fmt(seconds: number): string { return formatDuration(seconds); }
}
